import React, { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, TrendingUp, TrendingDown, Mic, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { API } from "../App";

/**
 * Compact adoption tile for the new Live Site Visit voice journaling flow.
 * Counts last 7 days, % delta vs prior 7 days, top 5 reps. Manager-only.
 */
export default function LiveVisitAdoptionCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/manager-dashboard/live-visit-adoption`);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card data-testid="live-visit-adoption-card">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-rose-500 animate-spin" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const { total_visits, total_clips, active_reps, delta_pct, top_reps } = data;
  const deltaPositive = delta_pct >= 0;
  const empty = total_visits === 0;

  return (
    <Card data-testid="live-visit-adoption-card" className="border-rose-100">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
            <MapPin className="w-5 h-5 text-rose-600" />
            Live Site Visits
            <Badge variant="secondary" className="ml-2 text-[10px] font-medium">7d</Badge>
          </CardTitle>
          {!empty && (
            <Badge
              className={`text-[11px] ${deltaPositive ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}
              data-testid="live-visit-delta"
            >
              {deltaPositive ? <TrendingUp className="w-3 h-3 mr-1 inline" /> : <TrendingDown className="w-3 h-3 mr-1 inline" />}
              {Math.abs(delta_pct)}% vs prev wk
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* KPI strip */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="rounded-lg bg-rose-50 border border-rose-100 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-wide text-rose-500 font-semibold">Visits</p>
            <p className="text-2xl font-bold text-rose-700 font-['Manrope']" data-testid="live-visit-total">{total_visits}</p>
          </div>
          <div className="rounded-lg bg-violet-50 border border-violet-100 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-wide text-violet-500 font-semibold">Clips</p>
            <p className="text-2xl font-bold text-violet-700 font-['Manrope']" data-testid="live-visit-clips">{total_clips}</p>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Active reps</p>
            <p className="text-2xl font-bold text-slate-700 font-['Manrope']" data-testid="live-visit-reps">{active_reps}</p>
          </div>
        </div>

        {/* Top reps leaderboard */}
        {empty ? (
          <div className="text-center py-4">
            <Mic className="w-7 h-7 text-slate-300 mx-auto mb-1" />
            <p className="text-xs text-slate-400">No live visits yet — share the feature with the team.</p>
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="live-visit-top-reps">
            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Top reps this week</p>
            {top_reps.map((r, i) => (
              <div
                key={r.user_id}
                className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-slate-50 transition-colors"
                data-testid={`live-visit-rep-${i}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[11px] font-semibold text-slate-400 w-4">#{i + 1}</span>
                  <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {r.picture ? (
                      <img src={r.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[10px] font-medium">{r.name?.charAt(0)}</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 truncate">{r.name}</p>
                </div>
                <div className="flex items-center gap-3 text-xs flex-shrink-0">
                  <span className="text-rose-600 font-semibold">{r.visits} visit{r.visits === 1 ? "" : "s"}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-violet-600">{r.clips} clip{r.clips === 1 ? "" : "s"}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
